import { AnyFunction } from '../type';
import { isDefined, isFunction } from '../typeGuard';

type InputType = string | number | symbol;
type HandlerMap<Key extends InputType, HandlerValue> = Record<Key, HandlerValue>;
type ExtraArguments<HandlerValue> = HandlerValue extends AnyFunction ? Parameters<HandlerValue> : [];
type Result<HandlerValue> = HandlerValue extends AnyFunction ? ReturnType<HandlerValue> : HandlerValue;

export type Handler<Match, HandlerValue> = (
    match: Match,
    ...args: ExtraArguments<HandlerValue>
) => Result<HandlerValue>;
export type HandlerWithDefault<Match, HandlerValue> = (
    match?: Match,
    ...args: ExtraArguments<HandlerValue>
) => Result<HandlerValue>;

export function createHandler<Match extends InputType, HandlerValue>(
    handlerMap: HandlerMap<Match, HandlerValue>
): Handler<Match, HandlerValue>;
export function createHandler<Match extends InputType, HandlerValue>(
    handlerMap: HandlerMap<Match, HandlerValue> | Partial<HandlerMap<Match, HandlerValue>>,
    otherwise: HandlerValue
): HandlerWithDefault<Match, HandlerValue>;
export function createHandler<Match extends InputType, HandlerValue>(
    handlerMap: HandlerMap<Match, HandlerValue> | Partial<HandlerMap<Match, HandlerValue>>,
    otherwise?: HandlerValue
) {
    return (match?: Match, ...args: ExtraArguments<HandlerValue>): Result<HandlerValue> => {
        const handler: HandlerValue | undefined = isDefined(match) ? handlerMap[match] : undefined;
        if (!isDefined(handler)) {
            if (!isDefined(otherwise)) {
                throw new TypeError(`No handler found for '${String(match)}' in '{${print(handlerMap)}}'.`);
            }

            return getResult(otherwise, args);
        }

        return getResult(handler, args);
    };
}

const getResult = <HandlerValue>(handle: HandlerValue, args: ExtraArguments<HandlerValue>): Result<HandlerValue> => {
    if (isFunction(handle)) {
        return (handle as AnyFunction)(...args) as Result<HandlerValue>;
    }

    return handle as Result<HandlerValue>;
};

const print = <Key extends InputType, HandlerValue>(
    handler: HandlerMap<Key, HandlerValue> | Partial<HandlerMap<Key, HandlerValue>>
) => Object.keys(handler).join(',');
